import React from 'react';
import {
  defineMessages,
  FormattedDate,
  FormattedMessage,
  FormattedTime,
  useIntl,
} from 'react-intl';
import { useUIDSeed } from 'react-uid';

import {
  Attachment,
  MessageNotification,
  ReferralMessageAttachment,
  ReportAppendixEventVerb,
  ReportEvent,
  ReportEventVerb,
  ReportKDBEventVerb,
  ReportVersionEventVerb,
  User,
  UserLite,
} from '../../../types';
import { Nullable } from '../../../types/utils';
import { getUserFullname } from '../../../utils/user';
import { Spinner } from '../../Spinner';
import { Attachments, Files } from './Attachments';
import { MailSentIcon, QuoteIcon } from '../../Icons';
import { EventMessage } from './EventMessage';
import { isEvent } from '../../../utils/styles';

const messages = defineMessages({
  someUser: {
    defaultMessage: 'Some user',
    description: `Default message to avoid erroring out when we are rendering
a newly created message and we are missing the current user.`,
    id: 'components.Message.someUser',
  },
  now: {
    defaultMessage: 'Just now',
    description:
      'Temporary indicator for the timing of a referral message which is being created.',
    id: 'components.Message.now',
  },
  sendingMessage: {
    defaultMessage: 'Sending message...',
    description:
      'Accessible message for the spinner while a message is being sent.',
    id: 'components.Message.sendingMessage',
  },
  attachmentsTitle: {
    defaultMessage: 'Attachments',
    description: 'Title for the list of attachments in a referral message.',
    id: 'components.Message.attachmentsTitle',
  },
  deletedUser: {
    defaultMessage: '"deleted user"',
    description: 'name of deleted user.',
    id: 'components.Message.deletedUser',
  },
});

const eventStyle = {
  [ReportVersionEventVerb.NEUTRAL]: {
    color: 'text-gray-600',
    border: 'border-l-2 border-gray-300 px-4',
  },
  [ReportVersionEventVerb.VERSION_ADDED]: {
    color: 'text-primary-600',
    border: 'border-l-2 border-dsfr-primary-500 px-4',
  },
  [ReportAppendixEventVerb.APPENDIX_ADDED]: {
    color: 'text-primary-600',
    border: 'border-l-2 border-dsfr-primary-500 px-4',
  },
  [ReportVersionEventVerb.VERSION_UPDATED]: {
    color: 'text-primary-600',
    border: 'border-l-2 border-dsfr-primary-500 px-4',
  },
  [ReportAppendixEventVerb.APPENDIX_UPDATED]: {
    color: 'text-primary-600',
    border: 'border-l-2 border-dsfr-primary-500 px-4',
  },
  [ReportVersionEventVerb.VERSION_VALIDATED]: {
    color: 'text-success-600',
    border: 'border-l-2 border-dsfr-success-500 px-4',
  },
  [ReportAppendixEventVerb.APPENDIX_VALIDATED]: {
    color: 'text-success-600',
    border: 'border-l-2 border-dsfr-success-500 px-4',
  },
  [ReportVersionEventVerb.MESSAGE]: {
    color: 'text-black',
    border: 'px-4 ',
  },
  [ReportVersionEventVerb.REQUEST_VALIDATION]: {
    color: 'text-gold-600',
    border: 'border-l-2 border-dsfr-warning-500 px-4',
  },
  [ReportAppendixEventVerb.APPENDIX_REQUEST_VALIDATION]: {
    color: 'text-gold-600',
    border: 'border-l-2 border-dsfr-warning-500 px-4',
  },
  [ReportAppendixEventVerb.APPENDIX_REQUEST_CHANGE]: {
    color: 'text-danger-600',
    border: 'border-l-2 border-dsfr-expert-500 px-4',
  },
  [ReportVersionEventVerb.REQUEST_CHANGE]: {
    color: 'text-danger-600',
    border: 'border-l-2 border-dsfr-expert-500 px-4',
  },
  [ReportKDBEventVerb.KDB_SEND_UPDATE]: {
    color: 'text-danger-600',
    border: 'border-l-2 border-danger-300 px-4',
  },
  [ReportKDBEventVerb.KDB_SEND_OVERRIDE]: {
    color: 'text-danger-600',
    border: 'border-l-2 border-danger-300 px-4',
  },
};

const iconStyle = {
  [ReportVersionEventVerb.NEUTRAL]: {
    color: 'fill-gray600',
  },
  [ReportVersionEventVerb.VERSION_ADDED]: {
    color: 'fill-dsfr-primary-500',
  },
  [ReportAppendixEventVerb.APPENDIX_ADDED]: {
    color: 'fill-dsfr-primary-500',
  },
  [ReportVersionEventVerb.VERSION_UPDATED]: {
    color: 'fill-dsfr-primary-500',
  },
  [ReportAppendixEventVerb.APPENDIX_UPDATED]: {
    color: 'fill-dsfr-primary-500',
  },
  [ReportVersionEventVerb.VERSION_VALIDATED]: {
    color: 'fill-dsfr-success-500',
  },
  [ReportAppendixEventVerb.APPENDIX_VALIDATED]: {
    color: 'fill-dsfr-success-500',
  },
  [ReportVersionEventVerb.MESSAGE]: {
    color: 'fill-black',
  },
  [ReportVersionEventVerb.REQUEST_VALIDATION]: {
    color: 'fill-dsfr-warning-500',
  },
  [ReportAppendixEventVerb.APPENDIX_REQUEST_VALIDATION]: {
    color: 'fill-dsfr-warning-500',
  },
  [ReportAppendixEventVerb.APPENDIX_REQUEST_CHANGE]: {
    color: 'fill-dsfr-danger-500',
  },
  [ReportVersionEventVerb.REQUEST_CHANGE]: {
    color: 'fill-dsfr-danger-500',
  },
  [ReportKDBEventVerb.KDB_SEND_UPDATE]: {
    color: 'fill-dsfr-danger-500',
  },
  [ReportKDBEventVerb.KDB_SEND_OVERRIDE]: {
    color: 'fill-dsfr-danger-500',
  },
};

interface MessageProps {
  created_at?: string;
  user: UserLite | Nullable<User>;
  verb?: ReportVersionEventVerb | ReportAppendixEventVerb | ReportKDBEventVerb;
  message: string;
  attachments?: ReferralMessageAttachment[] | File[];
  isProcessing?: boolean;
  notifications?: MessageNotification[];
  version?: {
    version_number: Nullable<number>;
  };
  appendix?: {
    appendix_number: Nullable<number>;
  };
  metadata?: ReportEvent['metadata'];
}

export const Message = ({
  isProcessing,
  created_at,
  user,
  verb = ReportVersionEventVerb.MESSAGE,
  message,
  version,
  appendix,
  attachments,
  notifications,
  metadata,
}: MessageProps) => {
  const seed = useUIDSeed();
  const intl = useIntl();
  const username = user
    ? getUserFullname(user)
    : intl.formatMessage(messages.deletedUser);

  const getBorder = (verb: string) => {
    return eventStyle.hasOwnProperty(verb)
      ? eventStyle[verb as ReportEventVerb].border
      : eventStyle[ReportVersionEventVerb.NEUTRAL].border;
  };

  const isKdbStatusUpdated = (verb: ReportEventVerb) => {
    return (
      verb === ReportKDBEventVerb.KDB_SEND_UPDATE ||
      verb === ReportKDBEventVerb.KDB_SEND_OVERRIDE
    );
  };

  const getFillColor = (verb: string) => {
    return iconStyle.hasOwnProperty(verb)
      ? iconStyle[verb as ReportEventVerb].color
      : iconStyle[ReportVersionEventVerb.NEUTRAL].color;
  };

  return (
    <li
      className="user-content flex flex-col w-full whitespace-pre-wrap mt-4 pl-2"
      data-testid="message-li"
    >
      <div className="flex flex-col space-y-1">
        <div className={`${getBorder(verb)}`}>
          {created_at ? (
            <span className="text-sm text-gray-500">
              <FormattedDate
                year="numeric"
                month="long"
                day="numeric"
                value={created_at}
              />
              {', '}
              <FormattedTime value={created_at} />
            </span>
          ) : (
            <span className="text-sm text-gray-500">
              <FormattedMessage {...messages.now} />
            </span>
          )}
          <div className={`flex w-fit`}>
            <div className="flex items-start leading-5">
              {(version || appendix || isKdbStatusUpdated(verb)) &&
              isEvent(verb) ? (
                <EventMessage
                  username={username}
                  metadata={metadata}
                  verb={verb}
                  version={version ? version.version_number : null}
                  appendix={appendix ? appendix.appendix_number : null}
                />
              ) : (
                <span className="font-medium">{username}</span>
              )}
              {isProcessing ? (
                <div>
                  <Spinner>
                    <FormattedMessage {...messages.sendingMessage} />
                  </Spinner>
                </div>
              ) : null}
            </div>
          </div>
        </div>
        <div>
          <div className="relative flex">
            <div className="relative flex flex-col">
              {message && (
                <div className="flex items-start space-x-2">
                  <QuoteIcon className="w-4 h-4" />
                  <p className="break-words">{message}</p>
                </div>
              )}
              {attachments && attachments.length > 0 ? (
                <div className="mt-3" style={{ width: '28rem' }}>
                  <h5
                    className="text-sm font-medium mb-1"
                    id={seed('message-attachments-list')}
                  >
                    <FormattedMessage {...messages.attachmentsTitle} />
                  </h5>
                  {isProcessing ? (
                    <Files
                      files={attachments as File[]}
                      labelId={seed('message-attachments-list')}
                    />
                  ) : (
                    <Attachments
                      attachments={attachments as Attachment[]}
                      labelId={seed('message-attachments-list')}
                    />
                  )}
                </div>
              ) : null}

              {notifications && notifications.length > 0 && (
                <div className="flex items-center pt-1 pl-6">
                  <MailSentIcon className={getFillColor(verb)} />
                  <div className="flex items-center">
                    {notifications.map((notification: MessageNotification) => {
                      return (
                        <span
                          key={notification.id}
                          className={`rounded-sm font-light text-sm ml-1`}
                        >
                          @{notification.notified.display_name}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </li>
  );
};
